/** `GenState`: the mutable world every generator pass works on.
 *
 * It lives in its own module rather than in `generate.ts` because the passes
 * that take it - `obstacleWalls.ts`, `thickets.ts` - are called BY `generate.ts`
 * and would have to import back from it for the type, which is an import cycle
 * and `.dependency-cruiser.cjs` rejects it. Nothing here depends on the pipeline
 * itself, only on the pieces the pipeline is built out of, so the split is along
 * a real seam and not a workaround.
 */

import type { AdventureMapState, ContentRegistry, MapObjectState } from "@game/core";
import type { Zone } from "./biomes.js";
import type { Grid } from "./grid.js";
import type { PlaceContext, Placement } from "./place.js";
import type { Rng } from "./rng.js";

export interface GenState {
  readonly r: Rng;
  /** Payload rolls only; see `makeSideRng`. Never a placement. */
  readonly side: Rng;
  readonly map: AdventureMapState;
  readonly g: Grid;
  readonly content: ContentRegistry;
  readonly ctx: PlaceContext;
  readonly objects: MapObjectState[];
  readonly placements: Placement[];
  readonly danger: Int32Array;
  readonly monsterBp: number;
  readonly zoneOf: Int32Array;
  readonly zones: readonly Zone[];
  /**
   * How many objects were minted before this state's first one. Object ids are
   * positional (`object:1`, `object:2`, ...) and the two levels are built into
   * separate lists that are concatenated at the end, so the underground's list
   * is numbered from where the surface's stops.
   */
  idBase: number;
}

/** Clamp to `[lo, hi]` and truncate: every knob and band in the generator is an integer. */
export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : Math.trunc(v);
